/**
@format
@flow
*/

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';
import Colors from '@ziynx/theme/colors';
import { Helmet } from 'react-helmet-async';
import { eventNames } from '@ziynx/analytics';
import ActionTypes from '@ziynx/action-types';
import Card from '@ziynx/components/cards';
import Link from '@ziynx/components/links';
import Headline from '@ziynx/components/headlines';
import { Heading } from '@ziynx/components/typography';
import { ContentContainer } from '@ziynx/components/layout';
import {
  withAppManager, LoadingSpinner, getGraduationInfo, getInitials, useNotification,
} from '@ziynx/components/utilities';
import type { ManagedProps } from '@ziynx/flow-types/appManager';

type History = {
  push: (pathName: string, params?: {
    userStatus?: string,
    title?: string,
  }) => void,
  replace: () => void,
  goBack: () => void,
};

type Location = {
  state: {
    title?: string,
    backLink?: string,
  },
};

type DispatchAction = {
  type: string,
  page?: number,
} => void;

type WatchlistResponse = {
  error: any,
  fetching: boolean,
  success: boolean,
  isLastPage: boolean,
  page: number,
  removeUser: {
    error: any,
    fetching: boolean,
    success: boolean,
  },
  results: Array<{
    _id: string,
    fullName: string,
    firstName: string,
    lastName: string,
    email: string,
    graduation: {
      month: number,
      year: number,
    },
    myShot: {
      sources: {
        thumbnail_optimized: {
          _id: string,
          url: string,
        },
        thumbnail: {
          _id: string,
          url: string,
        }
      }
    }
  }>;
};

type Props = $ReadOnly<{||}>;

const PageWrapper = styled.div`
  padding: ${({ isMobileSmall }) => (isMobileSmall ? '40px 16px' : '40px')};
  width: 100%;
  max-width: 688px;
  box-sizing: border-box;
  ::-webkit-scrollbar-track, ::-webkit-scrollbar-thumb, ::-webkit-scrollbar  {
    background-color: transparent;
  }
  ::-webkit-scrollbar {
    display: none;
  }
`;

const LinkWrapper = styled.div`
  padding-bottom: 24px;
`;

const CardWrapper = styled.div`
${({ lastChild }) => (lastChild ? 'margin-bottom: 100px;' : 'padding-bottom: 24px;')}
  width: ${({ isNonDesktop }) => (isNonDesktop ? '100%' : '688px')};
`;

const HeadlineWrapper = styled.div`
  padding-bottom: 40px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const BANNER_MESSAGE = {
  successRemove: 'has been removed from your watchlist.',
  error: 'An error occurred. Please try again later.',
  removing: 'Removing member.',
};

const WatchlistScreen = (props: ManagedProps<Props>): React$Node => {
  const { viewport } = props;
  const isMobileSmall = viewport === 'mobileSmall';
  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';

  const history: History = useHistory();
  const location: Location = useLocation();
  const dispatch: DispatchAction = useDispatch();
  const [talentName, setTalentName] = useState('');
  const { notificationDispatch } = useNotification();

  const watchlistService: WatchlistResponse = useSelector(({ watchlist }) => (
    {
      ...watchlist[ActionTypes.watchlist.get.GROUP] || {},
      removeUser: watchlist[ActionTypes.watchlist.remove.GROUP] || {},
    }
  ), shallowEqual);

  const resetNotification = () => {
    setTimeout(() => {
      dispatch({
        type: ActionTypes.watchlist.clearRemove,
      });
    }, 6000);
  };

  useEffect(() => {
    dispatch({
      type: ActionTypes.watchlist.get.START,
      page: 0,
    });
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_watchlist,
    });
  }, []);

  useEffect(() => {
    if (watchlistService?.removeUser?.success
      && !watchlistService?.removeUser?.fetching
      && talentName) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          variant: 'success',
          content: `${talentName} ${BANNER_MESSAGE?.successRemove}`,
        },
      });
      resetNotification();
    }
    if (watchlistService?.removeUser?.error && !watchlistService?.removeUser?.fetching) {
      notificationDispatch({
        type: ActionTypes.notifications.add.START,
        payload: {
          content: BANNER_MESSAGE.error,
          variant: 'error',
        },
      });
      resetNotification();
    }
  }, [watchlistService?.removeUser?.success,
    !watchlistService?.removeUser?.fetching,
    watchlistService?.removeUser?.error,
    talentName]);

  const onPaginate = (inView): void => {
    if (watchlistService?.isLastPage || watchlistService?.fetching || !inView) return;
    dispatch({
      type: ActionTypes.watchlist.get.START,
      page: watchlistService?.page + 1,
    });
  };

  const removeFromList = (_id, fullName) => {
    setTalentName(fullName);
    dispatch({
      type: ActionTypes.watchlist.remove.START,
      body: {
        userId: _id,
      },
      userInfo: { _id },
    });
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.removed_talent_from_watchlist,
      properties: {
        talentId: _id,
      },
    });
  };

  const renderCards = () => {
    if (!watchlistService?.results?.length) return null;

    return (watchlistService?.results ?? []).map((item, index) => {
      if (!Object.keys(item).length || !item) return null;

      const fullName = `${item?.firstName ?? ''} ${item?.lastName ?? ''}`;
      return (
        <CardWrapper
          key={`talent_${item?._id}`}
          isNonDesktop={viewport !== 'desktopSmall' && viewport !== 'desktop'}
          lastChild={index === watchlistService?.results?.length - 1}
        >
          {/* $FlowFixMe */}
          <Card
            variant="squircle"
            title={fullName}
            subtitle={getGraduationInfo(item?.graduation)}
            squircle={{
              squircleText: getInitials(`${item?.firstName} ${item?.lastName}`),
              imageSource: item?.myShot?.sources?.thumbnail_optimized?.url
              || item?.myShot?.sources?.thumbnail?.url,
              color: Colors.GrayVeryDark,
            }}
            rightAction={{
              popover: {
                position: 'bottomLeft',
                links: [
                  {
                    children: 'View talent',
                    onClick: () => history.push(`/discover/talent/${item?._id}`, { from: 'Watchlist' }),
                  },
                  {
                    children: 'Email talent',
                    href: `mailto: ${item?.email}`,
                    target: '_blank',
                    onClick: () => dispatch({
                      type: ActionTypes.analytics.logEvent.START,
                      name: eventNames.emailed_talent,
                    }),
                  },
                  // {
                  //   children: 'Copy phone number',
                  //   href: `mailto: ${item?.email}`,
                  // },
                  {
                    children: 'Remove from list',
                    color: Colors.SecondaryRed,
                    onClick: () => removeFromList(item?._id, fullName),
                  },
                ],

              },
            }}
          />
        </CardWrapper>

      );
    });
  };

  return (
    <>
      <Helmet>
        <title>Watchlist | ZIYNX</title>
      </Helmet>

      <ContentContainer offsetTop={isMobile ? 86 : 96}>
        <PageWrapper isMobileSmall={isMobileSmall}>
          <LinkWrapper>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link
              variant="caret"
              onClick={() => history.push(
                location?.state?.backLink || '/discover',
                { title: location?.state?.title, from: 'Watchlist' },
              )}
              color={Colors.GrayDark}
            >
              Back to&nbsp;
              {location.state?.title || 'Discover'}
            </Link>
          </LinkWrapper>
          <HeadlineWrapper>
            <Headline
              variant="page"
              title={{
                text: 'Watchlist',
                color: Colors.GrayVeryDark,
              }}
            />
          </HeadlineWrapper>
          {!watchlistService?.results?.length && !watchlistService?.fetching && (
            <Heading medium color={Colors.GrayDark}>
              List is empty
            </Heading>
          )}

          {renderCards()}
          <InView as="div" onChange={onPaginate}>
            {(!watchlistService?.isLastPage || watchlistService?.fetching) && (
              <SpinnerWrapper>
                <LoadingSpinner />
              </SpinnerWrapper>
            )}

          </InView>

        </PageWrapper>

      </ContentContainer>

    </>
  );
};

export default (withAppManager(WatchlistScreen): React$ComponentType<ManagedProps<Props>>);
